import { Injectable } from "@angular/core";
import { BehaviorSubject, filter, map, Observable, ReplaySubject, tap } from "rxjs";
import { ICriterion } from "../../distribute-team-members/models/criterion";
import { Category } from "../models/category.model";
import { Process } from "../models/process.model";
import { Question } from "../models/question.model";
import { HttpClient } from "@angular/common/http";
import { MemberModel } from "../models/member.model";
import { DecisionInput } from "../models/decision-input.model";
import { CopyModel } from "../models/copy.model";
import { ExecuteVisitResult } from "../models/execute-visit-result.model";
import { FormioAppConfig, ResourceNameService } from "dp-formio";

export const PAGE_SIZE = 5;
@Injectable({ providedIn: 'root' })
export class CriterionsService {
    private baseUrl = this.formioAppConfig.gateway;

    process$ = new ReplaySubject<Process>(1);
    criterion$ = new BehaviorSubject<ICriterion>(null);
    questionResult$ = new BehaviorSubject<Question>(null);
    decisionInputs$ = new BehaviorSubject<DecisionInput>(null);
    decisionTaken$ = new BehaviorSubject<void>(null);
    hasMistake$ = new BehaviorSubject<boolean>(null);
    sendEvaluation$ = new BehaviorSubject<CopyModel>(null);
    sendToExecutiveSummary$ = new BehaviorSubject<CopyModel>(null);
    sendSummary$ = new BehaviorSubject<CopyModel>(null);

    results$ = new BehaviorSubject<ExecuteVisitResult>(new ExecuteVisitResult());

    forms = {
        attachmentsFormKey: ''
    };

    constructor(private resourceNameService: ResourceNameService,
        private formioAppConfig: FormioAppConfig,
        private http: HttpClient) {
    }

    getCategory(criterionId: string): Observable<Category> {
        return this.resourceNameService.loadCustomByKeyId(this.formioAppConfig.endpoints.assessmentResource.categories, criterionId)
            .pipe(
                map((result: any) => {
                    const category: Category = {
                        ...result.data,
                        _id: result._id
                    };
                    if (result.data.numberOfApplicable > 0)
                        category.calculatedSavedScore = result.data.totalEvaluationCriteria / result.data.numberOfApplicable;
                    return category;
                })
            );
    }
    getQuestion(criterionId: string): Observable<Question> {
        return this.resourceNameService.loadCustomByKeyId(this.formioAppConfig.endpoints.assessmentResource.questions, criterionId)
            .pipe(
                map((result: any) => {
                    const question: Question = {
                        ...result.data,
                        _id: result._id,
                    };
                    question.hasLink = !!question.linkNameAr || !!question.linkNameEn;
                    question.hasLinkTooltip = !!question.tooltipAr || !!question.tooltipEn;

                    question.hasNotes = !!question.notesNameAr || !!question.notesNameEn;
                    question.hasNotesTooltip = !!question.notesTooltipAr || !!question.notesTooltipEn;

                    question.hasAttachments = !!question.attachments.length;
                    question.hasAttachmentsTooltip = !!question.attachmentsTooltipAr || !!question.attachmentsTooltipEn;
                    question.attachments?.forEach(attachment => attachment.hasTooltip = !!attachment.attachmentTooltipAr || !!attachment.attachmentTooltipEn);

                    question.hasPledgeAndAcknowledgment = !!question.pledgeNameAr || !!question.pledgeNameEn;
                    question.hasPledgeTooltip = !!question.pledgeTooltipAr || !!question.pledgeTooltipEn;

                    question.evaluationItems?.forEach(item => {
                        item.hasTooltip = !!item.tooltipAr || !!item.tooltipEn;
                        item.score = +item.score;
                    });
                    return question;
                })
            );
    }
    getProccess(accreditationKey: string, eligibilityRequestId: string): Observable<Process> {
        // accreditationKey = 'ERAV';
        // eligibilityRequestId = '1059';
        const query = `data.key=${accreditationKey}&data.requestId=${eligibilityRequestId}`;
        return this.resourceNameService.loadCustomByFilterdKey(this.formioAppConfig.endpoints.assessmentResource.process, query)
            .pipe(
                filter((data: any) => !!data.length),
                map(result => {
                    const process: Process = {
                        ...result[0].data,
                        _id: result[0]._id
                    };
                    return process;
                }),
                tap((data: any) => this.process$.next(data))
            );
    }

    //members selector
    getSessionMembers(visitKey: string, sessionId: string | number): Observable<MemberModel[]> {
        const url = `${this.baseUrl}${this.formioAppConfig.endpoints.visitMembers}visit/${visitKey}/session/${sessionId}`;
        return this.http.get<MemberModel[]>(url, {
            params: {
                page: '0',
                size: `${Number.MAX_SAFE_INTEGER}`,
            },
        });
    }
    
    //members selector
    getCriterionMembers(visitKey: string, criterionKey: string): Observable<MemberModel[]> {
        const url = `${this.baseUrl}${this.formioAppConfig.endpoints.criterion}${criterionKey}/visit/${visitKey}/members`;
        return this.http.get<MemberModel[]>(url, {
            params: {
                page: '0',
                size: `${Number.MAX_SAFE_INTEGER}`,
            },
        });
    }
}

